// Iter 88 — Autonomous Bets page (Phase F: single-bet real execution)
//
// Shows the user-level guardrail config, lets them toggle the master
// kill-switch, and lists every auto-bet with its state-machine timeline
// and current P&L.  Does NOT compose new bets here — those come from
// Schedule / Match Details via the AutoBetTriggerButton component.
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import TopNav from "../components/TopNav";

const STATE_BADGE = {
  draft:     { label: "Draft",     cls: "bg-slate-500/20 text-slate-200" },
  validated: { label: "Validated", cls: "bg-blue-500/20 text-blue-300" },
  queued:    { label: "Queued",    cls: "bg-indigo-500/20 text-indigo-300" },
  placing:   { label: "Placing",   cls: "bg-amber-500/20 text-amber-300" },
  placed:    { label: "Placed",    cls: "bg-emerald-500/20 text-emerald-300" },
  settled:   { label: "Settled",   cls: "bg-emerald-600/30 text-emerald-200" },
  rejected:  { label: "Rejected",  cls: "bg-rose-500/20 text-rose-300" },
  failed:    { label: "Failed",    cls: "bg-rose-600/30 text-rose-200" },
};

const VERDICT_BADGE = {
  won:  { label: "WON",  cls: "bg-emerald-600 text-white" },
  lost: { label: "LOST", cls: "bg-rose-600 text-white" },
  void: { label: "VOID", cls: "bg-slate-500 text-white" },
};

export default function AutoBetPage() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, led, pos] = await Promise.all([
        axios.get(`${API}/auto-bet/config`, { headers: authHeaders }),
        axios.get(`${API}/auto-bet/ledger`, { headers: authHeaders }),
        axios.get(`${API}/auto-bet/positions?limit=100`, { headers: authHeaders }),
      ]);
      setConfig(cfg.data);
      setDraft(cfg.data);
      setLedger(led.data);
      setPositions(pos.data.items || []);
    } catch (e) {
      toast.error("Failed to load auto-bet data");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) load();
    const t = setInterval(() => token && load(), 15000);
    return () => clearInterval(t);
  }, [token, load]);

  const toggleEnabled = async (val) => {
    try {
      const r = await axios.post(
        `${API}/auto-bet/config/enable`,
        { enabled: val },
        { headers: authHeaders }
      );
      setConfig(r.data);
      setDraft(r.data);
      toast.success(val ? "Auto-bet ENABLED" : "Auto-bet disabled");
    } catch {
      toast.error("Failed to toggle");
    }
  };

  const saveConfig = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const r = await axios.post(`${API}/auto-bet/config`, draft, { headers: authHeaders });
      setConfig(r.data);
      setDraft(r.data);
      toast.success("Config saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const settle = async (betId, verdict) => {
    try {
      await axios.post(
        `${API}/auto-bet/positions/${betId}/settle`,
        { verdict },
        { headers: authHeaders }
      );
      toast.success(`Marked ${verdict.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Settle failed");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100">
        <TopNav />
        <div className="max-w-xl mx-auto mt-20 p-6 text-center">
          Please sign in to use autonomous betting.
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="auto-bet-page">
      <TopNav />
      <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              Autonomous Bets <span className="text-emerald-400">·</span>{" "}
              <span className="text-sm font-normal text-slate-400">Phase F · single-bet live</span>
            </h1>
            <p className="text-slate-400 text-sm mt-1 max-w-2xl">
              Real-money bets placed on Betway after a strict risk-gate pipeline.
              The first {config?.dry_run_first_n ?? 3} bets execute as{" "}
              <span className="text-amber-300">booking codes</span> (no money moves)
              so you can inspect the slip before enabling live mode.
            </p>
          </div>
          <div className="flex items-center gap-3 bg-slate-900/70 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-slate-400">Master switch</div>
              <div className={`text-xl font-bold ${config?.enabled ? "text-emerald-400" : "text-rose-400"}`}>
                {config?.enabled ? "ARMED" : "SAFE"}
              </div>
            </div>
            <Switch
              data-testid="auto-bet-enabled-switch"
              checked={!!config?.enabled}
              onCheckedChange={toggleEnabled}
            />
          </div>
        </div>

        {/* Ledger */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="auto-bet-ledger">
          <Kpi label="Total bets" value={ledger?.total_bets ?? 0} />
          <Kpi label="Open" value={ledger?.open_positions ?? 0} />
          <Kpi label="Hit rate" value={`${ledger?.hit_rate_pct ?? 0}%`} tone={ledger?.hit_rate_pct >= 50 ? "pos" : "neg"} />
          <Kpi label="ROI" value={`${ledger?.roi_pct ?? 0}%`} tone={ledger?.roi_pct >= 0 ? "pos" : "neg"} />
          <Kpi label="PnL" value={ledger?.realised_pnl ?? 0} tone={ledger?.realised_pnl >= 0 ? "pos" : "neg"} />
          <Kpi label="24h loss" value={ledger?.daily_realised_loss ?? 0} tone="neg" />
        </div>

        {/* Config */}
        <Card className="bg-slate-900/70 border-slate-800">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Guardrails</CardTitle>
            <Button
              data-testid="auto-bet-save-config"
              size="sm" onClick={saveConfig} disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-500"
            >{saving ? "Saving…" : "Save"}</Button>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <NumField draft={draft} setDraft={setDraft} field="max_stake_per_bet" label="Max stake / bet" step="1" />
            <NumField draft={draft} setDraft={setDraft} field="daily_loss_stop" label="Daily loss stop" step="10" />
            <NumField draft={draft} setDraft={setDraft} field="min_odds" label="Min odds" step="0.05" />
            <NumField draft={draft} setDraft={setDraft} field="max_odds" label="Max odds" step="0.1" />
            <NumField draft={draft} setDraft={setDraft} field="max_open_positions" label="Max open" step="1" />
            <NumField draft={draft} setDraft={setDraft} field="min_quantum_confidence" label="Min Quantum conf" step="1" />
            <NumField draft={draft} setDraft={setDraft} field="min_event_match_confidence" label="Min match conf (0-1)" step="0.01" />
            <NumField draft={draft} setDraft={setDraft} field="dry_run_first_n" label="Dry-run first N" step="1" />
          </CardContent>
          <div className="px-6 pb-4 text-xs text-slate-500">
            Allowed markets:{" "}
            <span className="text-slate-300">{(config?.allowed_markets || []).join(" · ")}</span>
          </div>
        </Card>

        {/* Positions */}
        <Card className="bg-slate-900/70 border-slate-800">
          <CardHeader>
            <CardTitle className="text-lg">Bets ({positions.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {loading && <div className="text-slate-500 text-sm">Loading…</div>}
            {!loading && positions.length === 0 && (
              <div className="text-slate-500 text-sm">
                No auto-bets yet. Trigger one from a Schedule match once you've armed the switch.
              </div>
            )}
            {positions.map((p) => {
              const req = p.request || {};
              const stateBadge = STATE_BADGE[p.state] || { label: p.state, cls: "bg-slate-500/20" };
              const verdict = p?.settlement?.verdict;
              const vBadge = verdict ? VERDICT_BADGE[verdict] : null;
              return (
                <div key={p.id} className="border border-slate-800 rounded-lg p-3 bg-slate-950/50"
                     data-testid={`auto-bet-row-${p.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                      <div className="font-semibold">
                        {req.home_team} <span className="text-slate-500">vs</span> {req.away_team}
                      </div>
                      <div className="text-xs text-slate-400">{req.league}</div>
                      <div className="text-sm mt-1">
                        <span className="text-slate-500">{req.market_label} · </span>
                        <span className="text-emerald-300">{req.pick}</span>
                        <span className="text-slate-500"> @ </span>
                        <span className="font-semibold">{req.odds}</span>
                        <span className="text-slate-500"> · stake </span>
                        <span className="font-semibold">{p.stake}</span>
                        {p.dry_run && (
                          <Badge className="ml-2 bg-amber-500/20 text-amber-300">DRY-RUN</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={stateBadge.cls}>{stateBadge.label}</Badge>
                      {vBadge && <Badge className={vBadge.cls}>{vBadge.label}</Badge>}
                      {p.state === "placed" && !verdict && (
                        <>
                          <Button size="sm" variant="outline"
                            data-testid={`settle-won-${p.id}`}
                            onClick={() => settle(p.id, "won")}>Mark Won</Button>
                          <Button size="sm" variant="outline"
                            data-testid={`settle-lost-${p.id}`}
                            onClick={() => settle(p.id, "lost")}>Mark Lost</Button>
                          <Button size="sm" variant="outline"
                            data-testid={`settle-void-${p.id}`}
                            onClick={() => settle(p.id, "void")}>Void</Button>
                        </>
                      )}
                    </div>
                  </div>
                  {Array.isArray(p.guard_reasons) && p.guard_reasons.length > 0 && (
                    <div className="mt-2 text-xs text-rose-300">
                      {p.guard_reasons.map((r, i) => <div key={i}>• {r}</div>)}
                    </div>
                  )}
                  {p.message && (
                    <div className="mt-2 text-xs text-slate-400">{p.message}</div>
                  )}
                  {(p.booking_code || p.betway_bet_id) && (
                    <div className="mt-2 text-xs text-emerald-300">
                      {p.booking_code && <>Booking code: <span className="font-mono">{p.booking_code}</span></>}
                      {p.betway_bet_id && <> · Betway id: <span className="font-mono">{p.betway_bet_id}</span></>}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const toneCls =
    tone === "pos" ? "text-emerald-400" : tone === "neg" ? "text-rose-400" : "text-slate-100";
  return (
    <div className="rounded-xl bg-slate-900/70 border border-slate-800 px-4 py-3">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}

function NumField({ draft, setDraft, field, label, step = "1" }) {
  if (!draft) return null;
  return (
    <div>
      <Label className="text-xs text-slate-400">{label}</Label>
      <Input
        type="number"
        step={step}
        data-testid={`field-${field}`}
        value={draft[field] ?? 0}
        onChange={(e) => setDraft({ ...draft, [field]: Number(e.target.value) })}
        className="bg-slate-950 border-slate-800 mt-1"
      />
    </div>
  );
}
